import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Header,
  HeaderName,
  HeaderGlobalBar,
  HeaderGlobalAction,
  SideNav,
  SideNavItems,
  SideNavLink,
  SideNavMenu,
  SideNavMenuItem,
  Content,
  SkipToContent,
} from '@carbon/react';
import type { CarbonIconType } from '@carbon/icons-react';
import {
  Dashboard,
  UserMultiple,
  Document,
  Receipt,
  ShoppingCart,
  Edit,
  Headset,
  DeliveryTruck,
  UserCertification,
  Calendar,
  Time,
  UserFollow,
  Money,
  Purchase,
  Finance,
  MachineLearning,
  Notification,
  Settings,
  Grid,
  ChartLineData,
  Report,
  SettingsAdjust,
  Radar,
  Search,
  DataBase,
  SplitScreen,
  Email,
  MailAll,
  ChartBar,
  Dashboard as DashboardIcon,
  Folder,
  Task,
  Timer,
  TrashCan as TrashIcon,
  Document as DocumentIcon,
  DocumentAdd,
  AiGenerate,
  BatchJob,
  Calendar as CalendarIcon,
  Globe,
} from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { useNotificationStore } from '@/stores/notification.store';
import { NotificationCenter } from '@/components/NotificationCenter';
import { useNotifications } from '@/hooks/useNotifications';
import '@/styles/elbahi.css';

interface NavSection {
  label: string;
  items: NavItem[];
}

interface NavItem {
  label: string;
  href: string;
  icon: CarbonIconType;
  badge?: string;
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'CRM & VENTES',
    items: [
      { label: 'Tableau de bord', href: '/', icon: Dashboard },
      { label: 'Clients', href: '/contacts', icon: UserMultiple },
      { label: 'Devis', href: '/quotes', icon: Document },
      { label: 'Commandes', href: '/orders', icon: ShoppingCart },
      { label: 'Contrats', href: '/contracts', icon: Edit },
      { label: 'Opportunités', href: '/opportunities', icon: ChartLineData },
      { label: 'Help Desk', href: '/helpdesk', icon: Headset },
      { label: 'Fournisseurs', href: '/suppliers', icon: DeliveryTruck },
    ],
  },
  {
    label: 'GESTION RH',
    items: [
      { label: 'Employés', href: '/employees', icon: UserCertification },
      { label: 'Congés', href: '/leaves', icon: Calendar },
      { label: 'Timesheets', href: '/timesheets', icon: Time },
      { label: 'Recrutement', href: '/recruitment', icon: UserFollow },
    ],
  },
  {
    label: 'FINANCE',
    items: [
      { label: 'Facturation', href: '/invoices', icon: Receipt },
      { label: 'Paiements', href: '/payments', icon: Purchase },
      { label: 'Salaires', href: '/payroll', icon: Money },
      { label: 'Banques', href: '/banks', icon: Finance },
    ],
  },
  {
    label: 'PROJETS & IA',
    items: [
      { label: 'Projets', href: '/projects', icon: Grid },
      { label: 'Rapports', href: '/reports', icon: Report },
      { label: 'Assistant IA', href: '/ai-assistant', icon: MachineLearning, badge: 'IA' },
    ],
  },
  {
    label: 'RADAR',
    items: [
      { label: 'Tableau de bord', href: '/radar', icon: Radar },
      { label: 'Nouvelle recherche', href: '/radar/search', icon: Search },
      { label: 'Prospects', href: '/radar/prospects', icon: DataBase },
      { label: 'Pipeline', href: '/radar/pipeline', icon: SplitScreen },
      { label: 'Séquences', href: '/radar/sequences', icon: Email },
      { label: 'Validation', href: '/radar/inbox', icon: MailAll },
      { label: 'Analytics', href: '/radar/analytics', icon: ChartBar },
    ],
  },
  {
    label: 'PULSE',
    items: [
      { label: 'Tableau de bord', href: '/pulse', icon: DashboardIcon },
      { label: 'Tous les projets', href: '/pulse/projects', icon: Folder },
      { label: 'Mes tâches', href: '/pulse/my-tasks', icon: Task },
      { label: 'Timesheets', href: '/pulse/timesheets', icon: Timer },
      { label: 'Corbeille', href: '/pulse/trash', icon: TrashIcon },
    ],
  },
  {
    label: 'STUDIO',
    items: [
      { label: 'Tableau de bord', href: '/studio', icon: DashboardIcon },
      { label: 'Tous les contenus', href: '/studio/contents', icon: DocumentIcon },
      { label: 'Nouveau contenu', href: '/studio/contents/new', icon: DocumentAdd },
      { label: 'Batch WooCommerce', href: '/studio/batch', icon: BatchJob },
      { label: 'Plan production', href: '/studio/plan', icon: CalendarIcon },
      { label: 'WordPress', href: '/studio/wordpress', icon: Globe },
    ],
  },
  {
    label: 'INSIGHTS',
    items: [
      { label: 'Tableau de bord', href: '/insights', icon: ChartBar },
      { label: 'Rapports', href: '/insights/reports', icon: DocumentIcon },
      { label: 'Générer un rapport', href: '/insights/generate', icon: DocumentAdd },
      { label: 'Configuration', href: '/insights/config', icon: SettingsAdjust },
    ],
  },
  {
    label: 'FLOW',
    items: [
      { label: 'Tableau de bord', href: '/flow', icon: DashboardIcon },
      { label: 'Posts', href: '/flow/posts', icon: DocumentIcon },
      { label: 'Calendrier', href: '/flow/calendar', icon: CalendarIcon },
      { label: 'Comptes sociaux', href: '/flow/accounts', icon: UserMultiple },
      { label: 'Statistiques', href: '/flow/stats', icon: ChartBar },
    ],
  },
  {
    label: 'ADMINISTRATION',
    items: [
      { label: 'Paramètres', href: '/settings', icon: SettingsAdjust },
    ],
  },
];

const COCKPIT_SECTION: NavSection = {
  label: 'COCKPIT',
  items: [
    { label: 'Dashboard CEO', href: '/cockpit',               icon: DashboardIcon },
    { label: 'Rentabilité',   href: '/cockpit/profitability',  icon: ChartLineData },
    { label: 'Trésorerie',    href: '/cockpit/cashflow',       icon: Money },
    { label: 'Équipe',        href: '/cockpit/team',           icon: UserMultiple },
    { label: 'Pipeline',      href: '/cockpit/pipeline',       icon: SplitScreen },
    { label: 'Assistant IA',  href: '/cockpit/assistant',      icon: MachineLearning },
    { label: 'Paramètres',   href: '/cockpit/settings',       icon: SettingsAdjust },
  ],
};

const COCKPIT_ROLES = new Set(['ADMIN', 'MANAGER']);

class PageErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: '' };
  }
  static getDerivedStateFromError(e: Error) {
    return { hasError: true, error: e.message };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem' }}>
          <h2 style={{ color: '#da1e28', marginBottom: '0.5rem' }}>Une erreur s'est produite</h2>
          <p style={{ fontSize: '13px', color: 'var(--cds-text-secondary)', marginBottom: '1rem' }}>
            {this.state.error}
          </p>
          <button
            style={{ padding: '8px 16px', background: '#062810', color: '#B7FE1A', border: 'none', cursor: 'pointer', borderRadius: '4px' }}
            onClick={() => this.setState({ hasError: false, error: '' })}
          >
            Réessayer
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { unreadCount, setOpen: openNotifPanel } = useNotificationStore();

  // Initialise SSE connection + load notifications
  useNotifications();

  // Radar badges (refetch toutes les 30s)
  const { data: radarProspectsData } = useQuery({
    queryKey: ['radar-badge-prospects'],
    queryFn: async () => {
      const { getProspects } = await import('@/lib/radarApi');
      return getProspects({}, 1, 1);
    },
    refetchInterval: 30000,
    staleTime: 15000,
  });
  const radarProspectsCount = radarProspectsData?.total ?? 0;

  const { data: radarInboxData } = useQuery({
    queryKey: ['radar-inbox-count'],
    queryFn: async () => {
      const { getInboxCount } = await import('@/lib/radarApi');
      return getInboxCount();
    },
    refetchInterval: 15000,
    staleTime: 10000,
  });
  const radarInboxCount = radarInboxData?.count ?? 0;

  const { data: pulseProjectsData } = useQuery({
    queryKey: ['pulse-badge-projects'],
    queryFn: async () => {
      const { listProjects } = await import('@/lib/pulseApi');
      return listProjects({ status: 'ACTIVE', limit: 1 });
    },
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const pulseProjectsCount = pulseProjectsData?.total ?? 0;

  const { data: studioDashboardData } = useQuery({
    queryKey: ['studio-badge-dashboard'],
    queryFn: async () => {
      const { getDashboard } = await import('@/lib/studioApi');
      return getDashboard();
    },
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const studioActionCount = studioDashboardData
    ? (studioDashboardData.pendingActions.awaitingReview +
       studioDashboardData.pendingActions.awaitingValidation +
       studioDashboardData.pendingActions.awaitingPublish)
    : 0;

  // Cockpit alertes (ADMIN/MANAGER uniquement)
  const isCockpitUser = COCKPIT_ROLES.has(user?.role ?? '');
  const { data: cockpitAlerts } = useQuery({
    queryKey: ['cockpit-badge-alerts'],
    queryFn: async () => {
      const { getAlerts } = await import('@/lib/cockpitApi');
      return getAlerts();
    },
    enabled: isCockpitUser,
    refetchInterval: 30000,
    staleTime: 15000,
  });
  const cockpitAlertsCount = (cockpitAlerts ?? []).filter((a) => !a.acknowledgedAt).length;

  const initials = user
    ? `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase()
    : 'U';

  const handleLogout = () => {
    void logout().then(() => navigate('/login'));
  };

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <SkipToContent href="#main-content" />

      {/* ── TOP HEADER ─────────────────────────────────────── */}
      <Header aria-label="ELBAHI ERP" className="elbahi-header">
        <HeaderName as="div" prefix="">
          <Link to="/" className="elbahi-header-logo">
            <span className="elbahi-logo-mark">E</span>
            <span className="elbahi-header-title">
              ELBAHI <span className="elbahi-header-sub">ERP v1.0</span>
            </span>
          </Link>
        </HeaderName>

        <HeaderGlobalBar>
          <HeaderGlobalAction
            aria-label={`Notifications (${unreadCount})`}
            tooltipAlignment="end"
            onClick={() => openNotifPanel(true)}
          >
            <span style={{ position: 'relative', display: 'flex' }}>
              <Notification size={20} />
              {unreadCount > 0 && (
                <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </span>
          </HeaderGlobalAction>

          <HeaderGlobalAction aria-label="Paramètres" tooltipAlignment="end">
            <Settings size={20} />
          </HeaderGlobalAction>

          <HeaderGlobalAction
            aria-label={user?.firstName ?? 'Profil'}
            tooltipAlignment="end"
            onClick={handleLogout}
          >
            <span className="avatar-initials">{initials}</span>
          </HeaderGlobalAction>
        </HeaderGlobalBar>
      </Header>

      {/* ── SIDE NAV ────────────────────────────────────────── */}
      <SideNav
        aria-label="Navigation principale"
        isFixedNav
        expanded
        isPersistent
        className="elbahi-sidenav"
      >
        <SideNavItems>
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <div className="sidenav-section-label">{section.label}</div>
              {section.items.map((item) => {
                // Badge dynamique pour les items Radar
                let dynamicBadge: string | undefined = item.badge;
                if (item.href === '/radar/prospects' && radarProspectsCount > 0) {
                  dynamicBadge = String(radarProspectsCount);
                }
                if (item.href === '/radar/inbox' && radarInboxCount > 0) {
                  dynamicBadge = String(radarInboxCount);
                }
                if (item.href === '/pulse/projects' && pulseProjectsCount > 0) {
                  dynamicBadge = String(pulseProjectsCount);
                }
                if (item.href === '/studio/contents' && studioActionCount > 0) {
                  dynamicBadge = String(studioActionCount);
                }
                return (
                  <SideNavLink
                    key={item.href}
                    as={Link}
                    to={item.href}
                    renderIcon={item.icon}
                    isActive={isActive(item.href)}
                    className={isActive(item.href) ? 'sidenav-link--active' : ''}
                  >
                    <span className="sidenav-link-content">
                      {item.label}
                      {dynamicBadge && (
                        <span className={item.href === '/radar/inbox' ? 'sidenav-badge sidenav-badge--orange' : 'sidenav-badge'}>
                          {dynamicBadge}
                        </span>
                      )}
                    </span>
                  </SideNavLink>
                );
              })}
            </div>
          ))}

          {/* Section COCKPIT — ADMIN/MANAGER uniquement */}
          {isCockpitUser && (
            <div>
              <div className="sidenav-section-label">COCKPIT</div>
              {COCKPIT_SECTION.items.map((item) => {
                const isDashboard = item.href === '/cockpit';
                const badge = isDashboard && cockpitAlertsCount > 0
                  ? String(cockpitAlertsCount)
                  : undefined;
                return (
                  <SideNavLink
                    key={item.href}
                    as={Link}
                    to={item.href}
                    renderIcon={item.icon}
                    isActive={isDashboard ? location.pathname === '/cockpit' : isActive(item.href)}
                    className={isActive(item.href) ? 'sidenav-link--active' : ''}
                  >
                    <span className="sidenav-link-content">
                      {item.label}
                      {badge && (
                        <span className="sidenav-badge sidenav-badge--orange">{badge}</span>
                      )}
                    </span>
                  </SideNavLink>
                );
              })}
            </div>
          )}
        </SideNavItems>
      </SideNav>

      {/* ── CONTENU ─────────────────────────────────────────── */}
      <Content id="main-content" className="elbahi-content">
        <PageErrorBoundary>
          <Outlet />
        </PageErrorBoundary>
      </Content>

      {/* ── NOTIFICATION PANEL ──────────────────────────────── */}
      <NotificationCenter />
    </>
  );
}
